import { tokens } from '../../theme/tokens';

const { surface, line, ink, accent, signal, radius, shadow, font } = tokens;

/**
 * Design tokens for the "command header" store/admin dashboards.
 *
 * Originally transcribed from `design_handoff_store_admin_dashboard/README.md`.
 * The names are kept for the dashboard components; the values now come from
 * `theme/tokens.ts`, so the dark command header, the KPI strip and the rest
 * of the app share one palette, one radius scale and one font.
 */
export const commandDashboard = {
  color: {
    outerCard: surface.sunken,
    outerCardBorder: line.strong,
    innerCard: surface.card,
    innerCardBorder: line.default,
    dividerStrong: line.hairline,
    dividerLight: line.faint,
    headerGround: surface.header,
    ink: ink.primary,
    inkSecondary: ink.body,
    inkMuted: ink.bodyAlt,
    inkSubtle: ink.muted,
    inkFaint: ink.faint,
    onNavyBright: ink.onDark,
    onNavyBrighter: ink.onDarkBright,
    accent: accent.main,
    accentHover: accent.hover,
    dangerText: signal.problem.ink,
    dangerTint: signal.problem.tint,
    dangerRow: signal.problem.row,
    warningText: signal.caution.ink,
    warningTint: signal.caution.tint,
    successText: signal.positive.ink,
    successTextBright: signal.positive.ink,
    successTint: signal.positive.tint,
    infoText: signal.info.ink,
    infoTint: signal.info.tint,
    dotRed: signal.problem.solid,
    dotAmber: signal.caution.solid,
    dotGreen: signal.positive.solid,
    dotBlue: signal.info.solid,
    dotSlate: signal.neutral.solid,
    barDefault: accent.border,
    barAccent: accent.main,
    barFailure: signal.problem.solid,
    onNavyFill: 'rgba(255,255,255,.06)',
    onNavyChip: 'rgba(255,255,255,.07)',
    onNavyBadge: 'rgba(255,255,255,.1)',
    onNavyChipBorder: 'rgba(255,255,255,.12)',
    onNavyButtonBorder: 'rgba(255,255,255,.22)',
    onNavyButtonHover: 'rgba(255,255,255,.14)',
  },
  font: {
    sans: font.sans,
  },
  radius: {
    outerCard: radius.panel,
    innerCard: radius.card,
    toggleShell: radius.control,
    chipButton: radius.small,
    toggleButton: radius.chip,
    tagBadge: radius.chip,
    countPill: radius.pill,
    bar: '4px 4px 0 0',
    dot: '50%',
  },
  shadow: {
    outerCard: shadow.card,
  },
} as const;

export type KpiFlag = 'none' | 'red' | 'amber' | 'muted';

export const kpiFlagColors: Record<KpiFlag, { value: string; delta: string }> = {
  none: { value: commandDashboard.color.ink, delta: commandDashboard.color.successTextBright },
  red: { value: commandDashboard.color.dangerText, delta: commandDashboard.color.dangerText },
  amber: { value: commandDashboard.color.ink, delta: commandDashboard.color.warningText },
  muted: { value: commandDashboard.color.inkSubtle, delta: commandDashboard.color.inkSubtle },
};

export const tagPalette: Record<string, { bg: string; text: string }> = {
  Running: { bg: commandDashboard.color.infoTint, text: commandDashboard.color.infoText },
  Scheduled: { bg: commandDashboard.color.infoTint, text: commandDashboard.color.infoText },
  Done: { bg: commandDashboard.color.successTint, text: commandDashboard.color.successText },
  Failed: { bg: commandDashboard.color.dangerTint, text: commandDashboard.color.dangerText },
  Price: { bg: commandDashboard.color.dangerTint, text: commandDashboard.color.dangerText },
  Rebate: { bg: commandDashboard.color.warningTint, text: commandDashboard.color.warningText },
  User: { bg: commandDashboard.color.dividerStrong, text: commandDashboard.color.inkMuted },
  Catalog: { bg: commandDashboard.color.dividerStrong, text: commandDashboard.color.inkMuted },
  Vendor: { bg: commandDashboard.color.infoTint, text: commandDashboard.color.infoText },
  Job: { bg: commandDashboard.color.dangerTint, text: commandDashboard.color.dangerText },
  Store: { bg: commandDashboard.color.successTint, text: commandDashboard.color.successText },
};
